import {
  companyName,
  demoteHeadings,
  photoUrl,
  policySections,
  readPolicyContent,
  sanitizeHtml,
} from "@1-now/booking-core";
import { getPrivacyPolicyContent } from "api/rental-agreement/rental-agreement";
import { useEffect, useState } from "react";
import { useData } from "data/data-provider";
import { formatPhone, phoneHref } from "utils/formatPhone";
import { sameTabLinks } from "utils/richLinks";
import { usePolicyText } from "utils/usePolicyText";

/**
 * 1NOW-10372: standalone, publicly linkable policy pages.
 *
 * Tenants have to hand these URLs to third parties — an SMS carrier will not
 * approve a campaign until it can open a privacy policy page carrying the SMS
 * disclosures — so both designs route /privacy-policy and /terms-and-conditions
 * here and both footers link to them.
 *
 * Content is the same company-authored copy the checkout modals show; it comes
 * from the public (unauthenticated) /api/company/content/ endpoint grouped by
 * policy type.
 */
export type PolicyKind = "privacy_policy" | "terms_condition";

interface PolicyPageProps {
  kind: PolicyKind;
  title: string;
}

const PolicyPage = ({ kind, title }: PolicyPageProps) => {
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const { ourCompany, companyDetails } = useData();

  useEffect(() => {
    const getContent = async () => {
      setLoading(true);
      try {
        // readPolicyContent: a failed request is said so, not shown as "No
        // content available"; a type the operator has not written is [].
        const read = readPolicyContent(await getPrivacyPolicyContent());
        setFailed(read == null);
        setContent(policySections(read, kind));
      } catch (error) {
        console.error("Error fetching content", error);
      } finally {
        setLoading(false);
      }
    };

    getContent();
  }, [kind]);

  // The package's placeholders: the deposit as money ("$150.00", never
  // "$null"), and nothing printed for a field the operator has not set. A
  // company record without a phone, email or address uses the footer's.
  const { text: resolveTokens, contacts } = usePolicyText();

  const name = companyName(companyDetails, ourCompany);
  const phone = formatPhone(contacts.phone);
  // The number is a tel: link, built the way the footer builds its own (1NOW-11114).
  const tel = phone ? phoneHref(contacts.phone) : "";
  const phoneText = tel ? (
    <a href={tel} className="underline">
      {phone}
    </a>
  ) : (
    phone
  );
  const contact =
    name && phone ? (
      <>Questions? Contact {name} at {phoneText}.</>
    ) : name ? (
      <>Questions? Contact {name}.</>
    ) : phone ? (
      <>Questions? Call {phoneText}.</>
    ) : null;

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mt-6 min-h-[60vh] rounded-lg bg-bg-color p-4">
        <div className="mb-8">
          <h1 className="text-center text-3xl font-bold text-text-color sm:text-4xl">
            {title}
          </h1>
        </div>

        {loading ? (
          <div className="col-span-full flex min-h-[40vh] items-center justify-center">
            <div className="spinner-border inline-block h-6 w-6 animate-spin rounded-full border-4 border-t-transparent border-secondary-100"></div>
          </div>
        ) : (
          <div className="col-span-12 container">
            <div className="flex flex-col gap-y-4">
              {content.length > 0 ? (
                content.map((item: any, i: number) => (
                  <div className="rounded-lg border border-muted" key={i}>
                    <div className="header rounded-lg bg-text-color/5 px-5 py-3">
                      <h2 className="text-lg text-text-color">
                        {i + 1}. {item?.title}
                      </h2>
                    </div>
                    <div className="header w-full border-t border-muted p-5">
                      <div className="grid w-full grid-cols-1 gap-3 rounded-lg">
                        <div
                          className="prose prose-sm ql-content break-words text-text-color"
                          style={{ maxWidth: "100%" }} // Manually remove the max width
                          dangerouslySetInnerHTML={{
                            // Sanitised, then the operator's headings come down a
                            // level: the page's title is its one h1 (1NOW-10615).
                            // {{phone}} and {{email}} are tel: and mailto: links, and
                            // those stay in this tab (1NOW-11114).
                            __html: sameTabLinks(
                              demoteHeadings(sanitizeHtml(resolveTokens(item?.content, { links: true }))),
                            ),
                          }}
                        />
                      </div>
                    </div>
                    {item?.content_picture && item?.content_picture !== "null" && (
                      <div className="header border-t border-muted p-5">
                        <div className="grid grid-cols-1 gap-3 rounded-lg">
                          <img
                            alt={title}
                            src={photoUrl(item?.content_picture) ?? undefined}
                            height={660}
                            width={600}
                            className="w-full rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Tenants who have not written this policy yet still land on a
                // real page rather than a 404 — the footer links are always
                // rendered, and a third party opening the URL needs to see
                // something coherent.
                <div className="text-center text-text-color">
                  <p>
                    {failed
                      ? "We couldn't load this policy. Please try again in a moment."
                      : "This policy has not been published yet."}
                  </p>
                  {!failed && contact && <p className="mt-2">{contact}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Route-ready wrappers. The router `pages` array takes plain
 * `ComponentType<Object>` entries, so the policy kind is bound here rather than
 * passed as a route prop — and both designs stay on the same two URLs.
 */
export const PrivacyPolicyPage = () => (
  <PolicyPage kind="privacy_policy" title="Privacy Policy" />
);

export const TermsAndConditionsPage = () => (
  <PolicyPage kind="terms_condition" title="Terms & Conditions" />
);

export default PolicyPage;
